import React, { useState } from 'react';
import SEO from '../components/SEO';
import './RewardlySupport.css';

const RewardlySupport = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState('');

  const toggleFaq = (faqId) => {
    setExpandedFaq(expandedFaq === faqId ? null : faqId);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create mailto link with form data
    const subject = encodeURIComponent(formData.subject || 'Rewardly AI Support Request');
    const body = encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
    );
    
    window.location.href = `mailto:roboencode@gmail.com?subject=${subject}&body=${body}`;
    
    setFormStatus('Opening your email client...');
    
    // Reset form after a delay
    setTimeout(() => {
      setFormData({ name: '', email: '', subject: '', message: '' });
      setFormStatus('');
    }, 2000);
  };

  return (
    <div className="rewardly-support-page">
      <SEO 
        title="Rewardly AI Support - Help Center & Contact Information"
        description="Get help with Rewardly AI. Find answers to common questions, contact support, report issues, and learn how to maximize your credit card rewards."
        keywords="Rewardly AI support, help center, customer service, contact support, troubleshooting, FAQ"
        url="/rewardly-ai-card-rewards/support"
      />

      <div className="hero-section">
        <div className="container">
          <h1>🛟 Rewardly AI Support</h1>
          <p className="subtitle">We're here to help you maximize your rewards!</p>
        </div>
      </div>

      <div className="content-section">
        <div className="container">
          
          <section className="contact-options">
            <h2>📞 Contact Us</h2>
            <div className="contact-grid">
              <div className="contact-card">
                <div className="contact-icon">📧</div>
                <h3>Email Support</h3>
                <p>Get help via email within 24-48 hours</p>
                <a href="mailto:roboencode@gmail.com" className="contact-link">roboencode@gmail.com</a>
              </div>

              <div className="contact-card">
                <div className="contact-icon">🐛</div>
                <h3>Report a Bug</h3>
                <p>Found an issue? Let us know!</p>
                <a href="mailto:roboencode@gmail.com?subject=Rewardly%20AI%20Bug%20Report" className="contact-link">roboencode@gmail.com</a>
              </div>
            </div>
          </section>

          <section className="faq-section">
            <h2>❓ Frequently Asked Questions</h2>
            
            <div className="faq-category">
              <h3>Getting Started</h3>
              <div className="faq-list">
                <div className={`faq-item ${expandedFaq === 'faq1' ? 'expanded' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFaq('faq1')}>
                    <h4>How do I add my credit cards?</h4>
                    <span className="faq-toggle">{expandedFaq === 'faq1' ? '−' : '+'}</span>
                  </div>
                  {expandedFaq === 'faq1' && (
                    <div className="faq-answer">
                      <p>Go to the "Cards" tab and tap the "+" button. You can either select from our database of 100+ cards or manually enter your card details. We only need the card name and reward structure - never your card number or sensitive information.</p>
                    </div>
                  )}
                </div>
                
                <div className={`faq-item ${expandedFaq === 'faq2' ? 'expanded' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFaq('faq2')}>
                    <h4>Is Rewardly AI really free?</h4>
                    <span className="faq-toggle">{expandedFaq === 'faq2' ? '−' : '+'}</span>
                  </div>
                  {expandedFaq === 'faq2' && (
                    <div className="faq-answer">
                      <p>Yes! Rewardly AI is completely free to download and use. All core features including AI recommendations, expense tracking, and card comparisons are available at no cost.</p>
                    </div>
                  )}
                </div>
                
                <div className={`faq-item ${expandedFaq === 'faq3' ? 'expanded' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFaq('faq3')}>
                    <h4>Do I need to create an account?</h4>
                    <span className="faq-toggle">{expandedFaq === 'faq3' ? '−' : '+'}</span>
                  </div>
                  {expandedFaq === 'faq3' && (
                    <div className="faq-answer">
                      <p>You can use Rewardly AI without an account, but signing in with Google enables cloud sync, backup, and access to personalized recommendations across devices.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="faq-category">
              <h3>Features & Functionality</h3>
              <div className="faq-list">
                <div className="faq-item">
                  <h4>How do smart card suggestions work?</h4>
                  <p>Our AI analyzes your expense category (dining, travel, shopping, etc.), compares it against all your cards' reward rates, and recommends the card that will earn you the most rewards or cashback for that specific purchase.</p>
                </div>
                
                <div className={`faq-item ${expandedFaq === 'faq4' ? 'expanded' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFaq('faq4')}>
                    <h4>Can I track recurring expenses?</h4>
                    <span className="faq-toggle">{expandedFaq === 'faq4' ? '−' : '+'}</span>
                  </div>
                  {expandedFaq === 'faq4' && (
                    <div className="faq-answer">
                      <p>Yes! You can add expenses with different frequencies: daily, weekly, monthly, quarterly, or annually. The app will automatically calculate your total spending and optimal card usage.</p>
                    </div>
                  )}
                </div>
                
                <div className={`faq-item ${expandedFaq === 'faq5' ? 'expanded' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFaq('faq5')}>
                    <h4>How accurate are the reward calculations?</h4>
                    <span className="faq-toggle">{expandedFaq === 'faq5' ? '−' : '+'}</span>
                  </div>
                  {expandedFaq === 'faq5' && (
                    <div className="faq-answer">
                      <p>We strive for 100% accuracy by regularly updating our card database. However, credit card terms can change, so we recommend verifying current rates with your card issuer before making decisions.</p>
                    </div>
                  )}
                </div>
                
                <div className={`faq-item ${expandedFaq === 'faq6' ? 'expanded' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFaq('faq6')}>
                    <h4>Does the app work offline?</h4>
                    <span className="faq-toggle">{expandedFaq === 'faq6' ? '−' : '+'}</span>
                  </div>
                  {expandedFaq === 'faq6' && (
                    <div className="faq-answer">
                      <p>Yes! Most features work offline. You can add expenses, view recommendations, and manage your cards without an internet connection. Cloud sync requires connectivity.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="faq-category">
              <h3>Privacy & Security</h3>
              <div className="faq-list">
                <div className={`faq-item ${expandedFaq === 'faq7' ? 'expanded' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFaq('faq7')}>
                    <h4>Is my financial data secure?</h4>
                    <span className="faq-toggle">{expandedFaq === 'faq7' ? '−' : '+'}</span>
                  </div>
                  {expandedFaq === 'faq7' && (
                    <div className="faq-answer">
                      <p>Absolutely. We never ask for your credit card numbers, CVV, or bank account information. We only store card names and reward structures. All data is encrypted and stored securely on your device and our servers (if you use cloud sync).</p>
                    </div>
                  )}
                </div>
                
                <div className={`faq-item ${expandedFaq === 'faq8' ? 'expanded' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFaq('faq8')}>
                    <h4>Do you sell my data?</h4>
                    <span className="faq-toggle">{expandedFaq === 'faq8' ? '−' : '+'}</span>
                  </div>
                  {expandedFaq === 'faq8' && (
                    <div className="faq-answer">
                      <p>Never. Your financial information is private and will never be sold to third parties. We only use your data to provide personalized recommendations within the app.</p>
                    </div>
                  )}
                </div>
                
                <div className={`faq-item ${expandedFaq === 'faq9' ? 'expanded' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFaq('faq9')}>
                    <h4>How do I delete my account?</h4>
                    <span className="faq-toggle">{expandedFaq === 'faq9' ? '−' : '+'}</span>
                  </div>
                  {expandedFaq === 'faq9' && (
                    <div className="faq-answer">
                      <p>Go to Settings → Account → Delete Account. This will permanently remove all your data from our servers. You can also email us at roboencode@gmail.com for assistance.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="faq-category">
              <h3>Technical Issues</h3>
              <div className="faq-list">
                <div className={`faq-item ${expandedFaq === 'faq10' ? 'expanded' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFaq('faq10')}>
                    <h4>The app is crashing. What should I do?</h4>
                    <span className="faq-toggle">{expandedFaq === 'faq10' ? '−' : '+'}</span>
                  </div>
                  {expandedFaq === 'faq10' && (
                    <div className="faq-answer">
                      <p>Try these steps: 1) Force close and restart the app, 2) Update to the latest version from the App Store, 3) Restart your device, 4) If the issue persists, contact us at roboencode@gmail.com with your device model and iOS version.</p>
                    </div>
                  )}
                </div>
                
                <div className={`faq-item ${expandedFaq === 'faq11' ? 'expanded' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFaq('faq11')}>
                    <h4>My data isn't syncing across devices</h4>
                    <span className="faq-toggle">{expandedFaq === 'faq11' ? '−' : '+'}</span>
                  </div>
                  {expandedFaq === 'faq11' && (
                    <div className="faq-answer">
                      <p>Ensure you're signed in with the same Google account on all devices. Check your internet connection and try manually syncing by pulling down on the main screen to refresh.</p>
                    </div>
                  )}
                </div>
                
                <div className={`faq-item ${expandedFaq === 'faq12' ? 'expanded' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFaq('faq12')}>
                    <h4>I can't find my card in the database</h4>
                    <span className="faq-toggle">{expandedFaq === 'faq12' ? '−' : '+'}</span>
                  </div>
                  {expandedFaq === 'faq12' && (
                    <div className="faq-answer">
                      <p>You can manually add any card by tapping "Add Custom Card" and entering the reward structure. If you'd like us to add it to our database, email us at roboencode@gmail.com with the card details.</p>
                    </div>
                  )}
                </div>

                <div className={`faq-item ${expandedFaq === 'faq13' ? 'expanded' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFaq('faq13')}>
                    <h4>How do I restore my purchases?</h4>
                    <span className="faq-toggle">{expandedFaq === 'faq13' ? '−' : '+'}</span>
                  </div>
                  {expandedFaq === 'faq13' && (
                    <div className="faq-answer">
                      <p>Go to Settings → Restore Purchases. If you're still having issues, email us at roboencode@gmail.com with your order ID or receipt.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="faq-category">
              <h3>Card Database & Updates</h3>
              <div className="faq-list">
                <div className={`faq-item ${expandedFaq === 'faq14' ? 'expanded' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFaq('faq14')}>
                    <h4>How often is the card database updated?</h4>
                    <span className="faq-toggle">{expandedFaq === 'faq14' ? '−' : '+'}</span>
                  </div>
                  {expandedFaq === 'faq14' && (
                    <div className="faq-answer">
                      <p>We update our database monthly and issue urgent updates when major banks announce significant changes to reward programs.</p>
                    </div>
                  )}
                </div>
                
                <div className={`faq-item ${expandedFaq === 'faq15' ? 'expanded' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFaq('faq15')}>
                    <h4>A card's rewards have changed. How do I update it?</h4>
                    <span className="faq-toggle">{expandedFaq === 'faq15' ? '−' : '+'}</span>
                  </div>
                  {expandedFaq === 'faq15' && (
                    <div className="faq-answer">
                      <p>We'll update it automatically in the next database refresh. For immediate changes, you can edit the card manually in your Cards list by tapping on it and selecting "Edit."</p>
                    </div>
                  )}
                </div>
                
                <div className={`faq-item ${expandedFaq === 'faq16' ? 'expanded' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFaq('faq16')}>
                    <h4>Which countries/regions are supported?</h4>
                    <span className="faq-toggle">{expandedFaq === 'faq16' ? '−' : '+'}</span>
                  </div>
                  {expandedFaq === 'faq16' && (
                    <div className="faq-answer">
                      <p>Currently, we support credit cards from India, USA, and select other countries. We're actively expanding our coverage. Check the app for the most current list.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="troubleshooting">
            <h2>🔧 Troubleshooting Guide</h2>
            <div className="troubleshooting-grid">
              <div className="trouble-item">
                <h4>App Won't Open</h4>
                <ul>
                  <li>Update to the latest version</li>
                  <li>Restart your device</li>
                  <li>Uninstall and reinstall the app</li>
                  <li>Check if your iOS/Android version is supported</li>
                </ul>
              </div>

              <div className="trouble-item">
                <h4>Login Issues</h4>
                <ul>
                  <li>Check your internet connection</li>
                  <li>Verify Google account credentials</li>
                  <li>Clear app cache (Settings → Storage)</li>
                  <li>Try signing in with a different method</li>
                </ul>
              </div>

              <div className="trouble-item">
                <h4>Recommendations Not Showing</h4>
                <ul>
                  <li>Ensure you've added at least one card</li>
                  <li>Add some expenses to analyze</li>
                  <li>Check that cards have reward rates entered</li>
                  <li>Refresh the app by pulling down</li>
                </ul>
              </div>

              <div className="trouble-item">
                <h4>Sync Not Working</h4>
                <ul>
                  <li>Verify you're signed in</li>
                  <li>Check internet connection</li>
                  <li>Force sync from Settings</li>
                  <li>Sign out and sign back in</li>
                </ul>
              </div>
            </div>
          </section>


          <section className="contact-support-cta">
            <div className="cta-box">
              <h2>Still Need Help?</h2>
              <p>Can't find what you're looking for? Our support team is ready to assist you.</p>
              
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Your name"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="subject">Subject *</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    placeholder="What can we help you with?"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows="6"
                    placeholder="Please describe your issue or question in detail..."
                  ></textarea>
                </div>
                
                <button type="submit" className="submit-btn">
                  📧 Send Message
                </button>
                
                {formStatus && <p className="form-status">{formStatus}</p>}
              </form>
              
              <p className="response-time">⏱️ Average response time: 24-48 hours</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default RewardlySupport;